import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import {
  LinkDinamico,
  LinkDinamicoPublicoResponse,
  LocalLinkDinamico,
  TIPOS_ABERTURA,
  TipoAberturaLink,
} from './links-dinamicos.model';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/link-dinamico`;

/** O erro fica local: sem os links, cada tela aparece sem eles, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/**
 * Endereço http(s) ou caminho do portal com uma barra só. A API já filtra; aqui é defesa,
 * porque o "Modal interno" põe a URL num iframe marcado como confiável
 * (`bypassSecurityTrustResourceUrl`) — um `javascript:` não pode chegar lá.
 */
export function urlPublicavel(url: string | null | undefined): boolean {
  const limpa = (url ?? '').trim();

  return /^https?:\/\//i.test(limpa) || (limpa.startsWith('/') && !limpa.startsWith('//'));
}

function aberturaDe(valor: string | null | undefined): TipoAberturaLink {
  return TIPOS_ABERTURA.find((tipo) => tipo === valor) ?? 'Mesma página';
}

function midia(id: number, formato: 'Icone' | 'Imagem', versao: string | null | undefined): string | null {
  const limpa = versao?.trim();

  return limpa
    ? `${RECURSO}/${id}/imagem?formato=${formato}&v=${encodeURIComponent(limpa)}`
    : null;
}

/**
 * Os links da API prontos para as telas. As mídias saem pela rota pública com a versão em
 * `?v=`: a URL é fixa e tem cache de 1 hora, e sem a versão uma arte trocada no adm demoraria a
 * aparecer. Por defesa, sai o link sem nome ou com URL fora de `urlPublicavel`.
 */
export function paraLinksDinamicos(resposta: readonly LinkDinamicoPublicoResponse[]): LinkDinamico[] {
  return resposta
    .filter((item) => item.desNomeLkd?.trim() && urlPublicavel(item.desUrlDestinoLkd))
    .map((item) => ({
      id: item.codLinkDinamicoLkd,
      nome: item.desNomeLkd.trim(),
      descricao: item.desDescricaoLkd?.trim() ?? '',
      textoAlternativo: item.desTextoAlternativoLkd?.trim() ?? '',
      corFundo: item.desCorFundoLkd || null,
      corIcone: item.desCorIconeLkd || null,
      locais: (item.locais ?? []) as LocalLinkDinamico[],
      urlDestino: item.desUrlDestinoLkd.trim(),
      tipoAbertura: aberturaDe(item.desTipoAberturaLkd),
      icone: midia(item.codLinkDinamicoLkd, 'Icone', item.desVersaoIconeLkd),
      imagem: midia(item.codLinkDinamicoLkd, 'Imagem', item.desVersaoImagemLkd),
    }));
}

/**
 * Os links dinâmicos que o órgão publica no portal. É raiz: uma requisição por carregamento
 * do portal atende as quatro telas (homepage, Serviços digitais, Todos os serviços e
 * Notícias), que só consomem `porLocal`. O portal não envia o órgão: o back-end o resolve
 * pelo host.
 *
 * Situação, exclusão e vigência são aplicadas pela API. Enquanto carrega e quando a leitura
 * falha, `porLocal` devolve lista vazia.
 */
@Service()
export class LinksDinamicosService {
  private readonly linksApi = httpResource<ResponseApi<LinkDinamicoPublicoResponse[]>>(() => ({
    url: RECURSO,
    context: ERRO_LOCAL(),
  }));

  readonly carregando = computed(() => this.linksApi.isLoading());

  /** O `error()` é consultado antes do `value()`, que lança em estado de erro. */
  private readonly links = computed<readonly LinkDinamico[]>(() => {
    if (this.linksApi.error() !== undefined) return [];

    return paraLinksDinamicos(this.linksApi.value()?.data ?? []);
  });

  /** Os links de um local, na ordem da API. */
  porLocal(local: LocalLinkDinamico): readonly LinkDinamico[] {
    return this.links().filter((link) => link.locais.includes(local));
  }
}
